import React, { useState } from "react";
import ParadigmSheet from "./ParadigmSheet";

/**
 * Admin-only panel shown in the Quran word detail view.
 * Displays a proposed conjugation match from the VerbConjugation table
 * (auto-detected via _find_source_sense_by_norm) and lets the admin
 * confirm or reject the link. Also shows and allows unlinking an
 * already-confirmed verb_sense.
 *
 * Props:
 *   word         — the word detail object { metadata: { verb_sense, id, … }, conjugation_match, … }
 *   loading      — bool; disables buttons while a request is in flight
 *   onLink(senseId)  — called when admin confirms a link
 *   onUnlink()       — called when admin removes an existing link
 *   isAdmin      — bool; passed through to ParadigmSheet for edit mode
 */
export default function QuranVerbLinkPanel({ word, loading, onLink, onUnlink, isAdmin }) {
  const meta = word?.metadata;
  const verbSense = meta?.verb_sense;       // already-confirmed link
  const conjMatch = word?.conjugation_match; // auto-detected candidate (not yet linked)

  const [showParadigm, setShowParadigm] = useState(false);

  // Nothing to show if no metadata, no link, and (non-admin has no candidate to act on)
  if (!meta) return null;
  if (!verbSense && !conjMatch) return null;
  // Non-admins only see the panel when there's a confirmed link
  if (!isAdmin && !verbSense) return null;

  return (
    <div className="mt-3 p-3 bg-indigo-50 rounded-xl border border-indigo-200">
      <p className="text-xs font-semibold text-indigo-700 mb-2">{isAdmin ? "Verb Link" : "Root Verb"}</p>

      {verbSense ? (
        /* ── Already linked — visible to all users ── */
        <div>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-arabic text-indigo-900 font-medium">{verbSense.word_text}</p>
              <p className="text-xs text-indigo-700 mt-0.5">{verbSense.definition}</p>
              {/* Show matched conjugation tags when available */}
              {conjMatch && conjMatch.sense_id === verbSense.id && conjMatch.tags && conjMatch.tags.length > 0 && (
                <p className="text-xs text-indigo-400 mt-0.5">{conjMatch.tags.join(", ")}</p>
              )}
              <p className="text-xs text-indigo-400 mt-0.5">Sense #{verbSense.id}</p>
            </div>
            <span className="shrink-0 mt-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-medium">Linked</span>
          </div>
          <div className="mt-2.5 flex gap-2">
            <button
              onClick={() => setShowParadigm(true)}
              className={`py-1.5 text-xs rounded-lg border border-indigo-300 text-indigo-600 hover:bg-indigo-100 transition-colors font-medium ${isAdmin ? "flex-1" : "w-full"}`}
            >
              View conjugations
            </button>
            {isAdmin && (
              <button
                onClick={onUnlink}
                disabled={loading}
                className="flex-1 py-1.5 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                Unlink
              </button>
            )}
          </div>
        </div>
      ) : isAdmin && conjMatch ? (
        /* ── Proposed match (not yet confirmed) ── */
        <div>
          <p className="text-xs text-indigo-500 mb-1.5">
            Auto-suggested
            {conjMatch.match_source === "word_db" && (
              <span className="ml-1 text-indigo-400">(via word DB)</span>
            )}
            {conjMatch.match_source === "conjugation" && (
              <span className="ml-1 text-indigo-400">(via conjugation)</span>
            )}
          </p>
          <div className="flex items-start justify-between gap-2 mb-2.5">
            <div>
              <p className="text-sm font-arabic text-indigo-900 font-medium">{conjMatch.word_text}</p>
              <p className="text-xs text-indigo-700 mt-0.5">{conjMatch.definition}</p>
              {conjMatch.tags && conjMatch.tags.length > 0 && (
                <p className="text-xs text-indigo-400 mt-0.5">{conjMatch.tags.join(", ")}</p>
              )}
              <p className="text-xs text-indigo-400 mt-0.5">Sense #{conjMatch.sense_id}</p>
            </div>
          </div>
          <button
            onClick={() => onLink(conjMatch.sense_id)}
            disabled={loading}
            className="w-full py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium"
          >
            {loading ? "Linking…" : "Link to this verb"}
          </button>
        </div>
      ) : null}

      {showParadigm && verbSense && (
        <ParadigmSheet
          senseId={verbSense.id}
          wordText={verbSense.word_text}
          isAdmin={isAdmin}
          onClose={() => setShowParadigm(false)}
        />
      )}
    </div>
  );
}
